import { TPpdApp } from "../../../App/PpdApp";
import { TPpdTaskBase } from "../../../App/PpdBaseTask";
import renderReactComponent from "../../../React/ReactRenderer";
import { DataGridProps } from "@ogaf/react.scripts/Components/DataGrid/DataGrid";

/**
 * Pomocné interface pro typovost dat z řádku gridu.
 */
export interface SearchResultUpper {
    /** Řadicí klíč výsledku. */
    SORTKEY: string;

    /** Cesta ke složce obsahující nalezenou položku. */
    CESTA: string;

    /** Název nalezené položky. */
    NAME: string;

    /** ID nalezené položky. */
    ID: number;

    /** Typ nalezené položky. */
    TYPE: string;

    /** Číslo verze nalezené položky. */
    VERSION: number;

    /** Datum začátku platnosti ve formátu ISO, nebo null. */
    VALID_FROM: string | null;

    /** ID složky obsahující nalezenou položku. */
    PARENT_ID: number;

    /** MIME typ nalezeného souboru, nebo null. */
    MIME_TYPE: string | null;

    /** Velikost nalezeného souboru v bajtech, nebo null. */
    FILESIZE: number | null;

    /** Typ odkazu podle search kontraktu. */
    LINK_TYPE: string;

    /** Název tasku na generování odkazu. */
    TASK_NAME: string;

    /** URL adresa odkazu */
    LINK: string;

    /** Určuje, zda má být titulek článku v náhledu skrytý. Hodnota 1 znamená skrytý titulek. */
    HIDDENTITLE: number | null;

    /** Pokud se jedná o link, obsahuje adresu, kam link odkazuje.*/
    URL: string;
}

/**
 * Třída SearchTask reprezentuje úlohu Search modulu Misc.
 * Rozšiřuje základní třídu TPpdTaskBase.
 */
export class SearchTask extends TPpdTaskBase {
    /**
     * Vykreslí React komponentu pro vyhledávací vstup a nastaví kontext úlohy do sessionStorage.
     *
     * @returns {void}
     */
    public renderSearchInput(): void {
        renderReactComponent('SearchInput', 'reactSearchInputRoot', {
            onSearchSuccess: this.renderSearchResults.bind(this)
        });
        sessionStorage.setItem('controllerPath', 'Search');
        sessionStorage.setItem('taskName', 'taskSearch');
    }

    /**
     * Zobrazí kontejner datové mřížky a vykreslí React komponentu s výsledky vyhledávání.
     *
     * @returns {void}
     */
    public renderSearchResults(): void {
        document.getElementById('reactDataGridRoot').classList.remove('d-none');
        renderReactComponent('DataGrid', 'reactDataGridRoot', {
            columns: [
                { dataField: "NAME", caption: 'Název', allowSorting: true },
                { dataField: "CESTA", caption: 'Umístění', allowSorting: true },
                { dataField: "VERSION", caption: 'Verze', type: 'Number' },
                { dataField: "TYPE", caption: 'Typ', allowSorting: true }
            ],
            className: 'search-page-datagrid',
            apiUrl: '/Search/GetGridData',
            requestConfig: {
                dataSource: 'MyDataSource',
                selectCond: '1=1',
                dataId: '',
                view: 'MyView'
            },
            title: 'Přehled záznamů',
            onRowDoubleClick: this.rowDoubleClickHandler,

        } as DataGridProps<SearchResultUpper>);
    }

    /**
     * Metoda obstarává dvojklik na řádek ve vyhledávacím gridu
     * @param res Data řádku gridu, na který uživatel dvojklikl.
     */
    public rowDoubleClickHandler(res: SearchResultUpper): void {
        const taskName = res.TASK_NAME.replace('task', '');
        const newUrl = `${window.location.origin}${ClientApplication.TaskMap.find(x => x.key == res.TASK_NAME).uri}index`;

        switch (res.TYPE) {
            case 'článek':
                const articleUrl = `${newUrl}?folderId=${res.PARENT_ID}&articleId=${res.ID}&articleVer=${res.VERSION}`;
                window.open(articleUrl, '_blank', 'noopener,noreferrer');
                break;
            case 'soubor':
                (globalThis.CurrentTask as SearchTask).DownloadFile(
                    String(res.ID),
                    String(res.VERSION),
                    res.NAME,
                    newUrl.replace("index", "") + "DigestMessage"
                );

                break;
            case 'složka':
                const folderUrl = `${newUrl}?folderId=${res.ID}`;
                window.open(folderUrl, '_blank', 'noopener,noreferrer');
                break;
            case 'odkaz':
                if (res.URL) {
                    window.open(res.URL, '_blank', 'noopener,noreferrer');
                }
                break;
            default:
                break;
        }
    }

    /**
     * Metoda obstarává stažení požadovaného souboru.
     * @param id Identifikátor požadovaného souboru.
     * @param version Verze požadovaného souboru.
     * @param name Název souboru.
     * @param url Adresa kontroleru, kam se posílá požadavek.
     */
    public async DownloadFile(id: string, version: string, name: string, url: string): Promise<void> {
        try {
            const res = await this.dispatchToServer("DownloadFile", {
                data: { Id: id, Version: version }
            },
            url);

            if (res.status === 403) {
                const msg = await res.text().catch(() => "");
                this.App.showMessageBoxEx("Oprávnění", msg || "Nemáte oprávnění tuto akci provést.", "", "", "Ok");
                return;
            }

            if (!res.ok) {
                const text = await res.text().catch(() => "");
                console.error("[DownloadFile] ERROR response text:", text);
                this.App.showMessageBoxEx("Upozornění", text || "Stažení se nezdařilo.", "", "", "Ok");
                return;
            }

            const blob = await res.blob();
            const link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = name;
            link.click();
            window.URL.revokeObjectURL(link.href);
        } catch (e) {
            console.error(e);
            this.App.showMessageBoxEx("Upozornění", "Stažení se nezdařilo.", "", "", "Ok");
        }
    }
}

/**
 * Inicializuje aplikaci a registruje úlohu SearchTask.
 */
ScriptInit(new TPpdApp(), new SearchTask());